// ─── Purchase-specific imports ────────────────────────────────────────────────
import React, { forwardRef, useMemo } from "react";
import { round2, fmtCurrency, T } from "../Sales/SalesShared";

// ─── Re-export everything from SalesShared ────────────────────────────────────
export {
    T, DocStatusBadge, fmtMYR, fmtCurrency, round2,
    rowPerms, StatCard, SortTh, SearchTypeDropdown,
    PreviewPanel, DeleteModal, CancelDocModal, SectionHeading, InfoRow,
    DetailRow, DetailSectionHeading,
    btnStyleOutline, btnStylePrimary, btnStyleDanger,
    ItemSearchSelect, FieldInput, SearchableSelect, SimpleSelect,
    Banner, CompanyStatusBanner, LineItemsTable,
    DiscardModal, ApproveModal, RejectModal,
} from "../Sales/SalesShared";

// ─── PURCHASE PRINT TEMPLATE ───────────────────────────────────────────────

/** Inline print styles — keeps all sizing logic co-located with the template. */
const pS = {
    page: { fontFamily: "'Helvetica Neue','Helvetica','Arial',sans-serif", fontSize: 12, color: "#333", background: "#fff", padding: "30px 40px", boxSizing: "border-box", width: "210mm", minHeight: "297mm", margin: "0 auto", boxShadow: "0 0 10px rgba(0,0,0,0.15)" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "15px", paddingBottom: "10px", borderBottom: "2px solid #eee" },
    companyDetails: { flex: 1 },
    companyName: { fontWeight: 700, fontSize: 20, color: "#000", marginBottom: 2 },
    companyInfo: { fontSize: 11, color: "#555", lineHeight: 1.5 },
    docInfo: { textAlign: "right", minWidth: 200 },
    docTitle: { fontSize: 28, fontWeight: "bold", color: "#252f3f", marginBottom: "10px" },
    docMeta: { fontSize: 11, color: "#555" },
    section: { marginTop: "15px", marginBottom: "15px" },
    sectionHeader: { padding: "4px 8px", background: "#252f3f", color: "#fff", fontSize: 11, fontWeight: "bold", marginBottom: "8px" },
    supplierDetails: { fontSize: 12, lineHeight: 1.6 },
    table: { width: "100%", borderCollapse: "collapse", marginTop: "15px", fontSize: 12 },
    th: { background: "#252f3f", color: "#fff", padding: "8px", textAlign: "left", fontWeight: "bold", textTransform: "uppercase", fontSize: 9, letterSpacing: "0.5px" },
    td: { padding: "8px", borderBottom: "1px solid #eee", verticalAlign: "middle" },
    remarks: { flex: 1, padding: "8px 10px", fontSize: 11, color: "#444", lineHeight: 1.5 },
    totalRow: { display: "flex", justifyContent: "space-between", padding: "5px 8px", borderBottom: "1px solid #eee", fontSize: 12 },
    totalLabel: { display: "table-cell", padding: "6px 10px", fontWeight: "bold", textAlign: "left" },
    grandTotalRow: { display: "flex", justifyContent: "space-between", padding: "8px", background: "#252f3f", color: "#fff", fontWeight: "bold", fontSize: 12 },
    signatureSection: { display: "flex", justifyContent: "space-between", marginTop: "60px", paddingTop: "15px", borderTop: "1px solid #eee" },
    signatureBox: { width: "48%", fontSize: 10, color: "#555" },
    signatureLine: { borderTop: "1px solid #aaa", marginTop: "60px", paddingTop: "5px" },
    footer: { textAlign: "center", marginTop: "15px", fontSize: 9, color: "#888", fontStyle: "italic" },
};

/** Format "2024-07-15" → "15/07/2024" for printed output. */
const fmtPrintDate = (d) => {
    if (!d) return "—";
    const [year, month, day] = d.split("-");
    return year && month && day ? `${day}/${month}/${year}` : d;
};

export const PurchasePrintTemplate = forwardRef(function PurchasePrintTemplate(
    { data = {}, companyProfile = {}, docType = "Purchase Order", taxRate = 0, forceNegativeTotals = false },
    ref
) {
    const isPV = docType === "Payment Voucher";
    const isPCN = docType === "Purchase Credit Note";
    const isPDN = docType === "Purchase Debit Note";
    const cp = companyProfile || {};

    // Filter out unselected items (keeps behaviour consistent with sales template)
    const items = useMemo(
        () => (data.items || []).filter((item) => item.selected !== false),
        [data.items]
    );

    // ── Totals ──────────────────────────────────────────────────────────────

    const { subtotal, taxAmt, grandTotal } = useMemo(() => {
        if (isPV) {
            const total = items.reduce((s, r) => s + round2(Number(r.payAmount) || 0), 0);
            return { subtotal: total, taxAmt: 0, grandTotal: total };
        }
        const sub = items.reduce((s, r) => s + round2((Number(r.qty) || 0) * (Number(r.unitPrice) || 0)), 0);
        const tax = round2(sub * (taxRate / 100));
        const grand = round2(sub + tax);
        return { subtotal: sub, taxAmt: tax, grandTotal: grand };
    }, [items, taxRate, isPV]);

    const displaySubtotal = forceNegativeTotals ? -Math.abs(subtotal) : subtotal;
    const displayTaxAmount = forceNegativeTotals ? -Math.abs(taxAmt) : taxAmt;
    const displayGrandTotal = forceNegativeTotals ? -Math.abs(grandTotal) : grandTotal;

    // Pad to at least 4 rows so the table doesn't look empty on small docs
    const minRows = 4;
    const emptyRows = useMemo(
        () => (items.length < minRows ? [...Array(minRows - items.length)] : []),
        [items.length]
    );

    // ── Grand total label ────────────────────────────────────────────────────

    const grandTotalLabel = isPV
        ? "TOTAL AMOUNT PAID"
        : isPCN
            ? `CREDIT AMOUNT (${data.currency || "MYR"})`
            : isPDN
                ? `DEBIT AMOUNT (${data.currency || "MYR"})`
                : `GRAND TOTAL (${data.currency || "MYR"})`;

    return (
        <div ref={ref} className="print-page" style={pS.page}>
            <style>{`
                @media print {
                    /* No page margin, so the browser has nowhere to print its date, title and
                       link header/footer. The same white space is kept as padding instead,
                       repeated on every page of a long document (box-decoration-break). */
                    @page { size: A4; margin: 0; }
                    .print-page {
                        box-shadow: none !important; margin: 0 !important;
                        padding: 10mm 15mm !important; width: 100% !important;
                        box-decoration-break: clone; -webkit-box-decoration-break: clone;
                        max-width: 100% !important; min-height: unset !important;
                        height: auto !important; box-sizing: border-box !important;
                    }
                    header, section, table, footer { page-break-inside: avoid !important; break-inside: avoid !important; }
                }
            `}</style>

            {/* ── Company header ────────────────────────────────────────── */}
            <header style={pS.header}>
                <div style={pS.companyDetails}>
                    {cp.LogoUrl && <img src={cp.LogoUrl} alt="logo" style={{ maxHeight: 50, marginBottom: 10 }} />}
                    <div style={pS.companyName}>{cp.CompanyName || "[Company Name]"}</div>
                    <div style={pS.companyInfo}>
                        {cp.Address && <div style={{ whiteSpace: "pre-line" }}>{cp.Address}</div>}
                        {cp.PhoneNumber && <div>Tel: {cp.PhoneNumber}</div>}
                        {cp.Email && <div>Email: {cp.Email}</div>}
                        {cp.RegNo && <div>Reg No: {cp.RegNo}</div>}
                        {cp.TaxNo && <div>Tax No: {cp.TaxNo}</div>}
                    </div>
                </div>

                <div style={pS.docInfo}>
                    <div style={pS.docTitle}>{docType.toUpperCase()}</div>
                    <table style={{ width: "100%" }}>
                        <tbody>
                            <tr>
                                <td style={{ ...pS.docMeta, textAlign: "right", fontWeight: "bold", paddingRight: 8 }}>DATE</td>
                                <td style={{ ...pS.docMeta, textAlign: "right" }}>{fmtPrintDate(data.date)}</td>
                            </tr>
                            <tr>
                                <td style={{ ...pS.docMeta, textAlign: "right", fontWeight: "bold", paddingRight: 8 }}>REF NO.</td>
                                <td style={{ ...pS.docMeta, textAlign: "right" }}>{data.id || "N/A"}</td>
                            </tr>
                            {data.deliveryDate && (
                                <tr>
                                    <td style={{ ...pS.docMeta, textAlign: "right", fontWeight: "bold", paddingRight: 8 }}>DELIVERY DATE</td>
                                    <td style={{ ...pS.docMeta, textAlign: "right" }}>{fmtPrintDate(data.deliveryDate)}</td>
                                </tr>
                            )}
                            {data.paymentTerms && (
                                <tr>
                                    <td style={{ ...pS.docMeta, textAlign: "right", fontWeight: "bold", paddingRight: 8 }}>TERMS</td>
                                    <td style={{ ...pS.docMeta, textAlign: "right" }}>{data.paymentTerms}</td>
                                </tr>
                            )}
                            {/* Purchase Invoices keep their PO in poRefNo. Purchase Credit/Debit
                                Notes keep a Purchase Invoice number in purchaseOrderId. */}
                            {data.poRefNo && (
                                <tr>
                                    <td style={{ ...pS.docMeta, textAlign: "right", fontWeight: "bold", paddingRight: 8 }}>PO REF.</td>
                                    <td style={{ ...pS.docMeta, textAlign: "right" }}>{data.poRefNo}</td>
                                </tr>
                            )}
                            {data.purchaseOrderId && (
                                <tr>
                                    <td style={{ ...pS.docMeta, textAlign: "right", fontWeight: "bold", paddingRight: 8 }}>{isPCN || isPDN ? "PI REF." : "PO REF."}</td>
                                    <td style={{ ...pS.docMeta, textAlign: "right" }}>{data.purchaseOrderId}</td>
                                </tr>
                            )}
                            {data.refNo && (
                                <tr>
                                    <td style={{ ...pS.docMeta, textAlign: "right", fontWeight: "bold", paddingRight: 8 }}>INV REF.</td>
                                    <td style={{ ...pS.docMeta, textAlign: "right" }}>{data.refNo}</td>
                                </tr>
                            )}
                            {isPV && data.paymentMethod && (
                                <tr>
                                    <td style={{ ...pS.docMeta, textAlign: "right", fontWeight: "bold", paddingRight: 8 }}>PAY METHOD</td>
                                    <td style={{ ...pS.docMeta, textAlign: "right" }}>{data.paymentMethod}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </header>

            {/* ── Supplier / Pay-to section ──────────────────────────────── */}
            <section style={pS.section}>
                <div style={{ display: "flex", gap: "20px" }}>
                    {/* Supplier (or Pay To) */}
                    <div style={{ flex: 1 }}>
                        <div style={pS.sectionHeader}>{isPV ? "PAY TO" : "SUPPLIER"}</div>
                        <div style={pS.supplierDetails}>
                            <div style={{ fontWeight: "bold" }}>{data.supplier || "—"}</div>
                            {data.supplierAddress
                                ? <div style={{ whiteSpace: "pre-line" }}>{data.supplierAddress}</div>
                                : <div style={{ color: "#999", fontStyle: "italic" }}>No billing address provided</div>}
                            {data.contactPerson && <div>Attn: {data.contactPerson}</div>}
                            {data.phoneNumber && <div>Tel: {data.phoneNumber}</div>}
                        </div>
                    </div>

                    {/* Ship-to (our company address) — shown for PO only */}
                    {docType === "Purchase Order" && data.shippingAddress && (
                        <div style={{ flex: 1 }}>
                            <div style={pS.sectionHeader}>SHIP TO</div>
                            <div style={pS.supplierDetails}>
                                <div style={{ fontWeight: "bold" }}>{cp.CompanyName || "—"}</div>
                                <div style={{ whiteSpace: "pre-line" }}>{data.shippingAddress}</div>
                            </div>
                        </div>
                    )}

                    {/* Pay From — shown for PV */}
                    {isPV && data.payFrom && (
                        <div style={{ flex: 1 }}>
                            <div style={pS.sectionHeader}>PAY FROM</div>
                            <div style={pS.supplierDetails}>
                                <div>{data.payFrom}</div>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* ── Line items table ───────────────────────────────────────── */}
            <table style={pS.table}>
                <thead>
                    {isPV ? (
                        <tr>
                            <th style={{ ...pS.th, width: "5%", textAlign: "center" }}>#</th>
                            <th style={{ ...pS.th, width: "30%" }}>Account Code / Category</th>
                            <th style={{ ...pS.th, width: "45%" }}>Description</th>
                            <th style={{ ...pS.th, width: "20%", textAlign: "right" }}>
                                Pay Amount ({data.currency || "MYR"})
                            </th>
                        </tr>
                    ) : (
                        <tr>
                            <th style={{ ...pS.th, width: "30px", textAlign: "center" }}>#</th>
                            <th style={{ ...pS.th, width: "15%" }}>Item Code</th>
                            <th style={{ ...pS.th, width: "auto" }}>Description</th>
                            <th style={{ ...pS.th, width: "8%", textAlign: "center" }}>Unit</th>
                            <th style={{ ...pS.th, width: "8%", textAlign: "right" }}>Qty</th>
                            <th style={{ ...pS.th, width: "12%", textAlign: "right" }}>Unit Price</th>
                            <th style={{ ...pS.th, width: "12%", textAlign: "right" }}>
                                Amount ({data.currency || "MYR"})
                            </th>
                        </tr>
                    )}
                </thead>
                <tbody>
                    {isPV
                        ? items.map((r, i) => (
                            <tr key={i}>
                                <td style={{ ...pS.td, textAlign: "center" }}>{i + 1}</td>
                                <td style={pS.td}>{r.accountCode || "—"}</td>
                                <td style={{ ...pS.td, wordBreak: "break-word", whiteSpace: "normal" }}>{r.description || "—"}</td>
                                <td style={{ ...pS.td, textAlign: "right", fontWeight: "bold" }}>
                                    {Number(r.payAmount || 0).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                            </tr>
                        ))
                        : items.map((r, i) => (
                            <tr key={i}>
                                <td style={{ ...pS.td, textAlign: "center" }}>{i + 1}</td>
                                <td style={pS.td}>{r.itemCode || ""}</td>
                                <td style={pS.td}>{r.description || ""}</td>
                                <td style={{ ...pS.td, textAlign: "center" }}>{r.unit || "—"}</td>
                                <td style={{ ...pS.td, textAlign: "right" }}>{r.qty || 0}</td>
                                <td style={{ ...pS.td, textAlign: "right" }}>
                                    {Number(r.unitPrice || 0).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td style={{ ...pS.td, textAlign: "right", fontWeight: "bold" }}>
                                    {round2((Number(r.qty) || 0) * (Number(r.unitPrice) || 0)).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                            </tr>
                        ))
                    }

                    {/* Empty padding rows */}
                    {emptyRows.map((_, i) => (
                        <tr key={`empty-${i}`}>
                            <td style={{ ...pS.td, height: "32px" }}>&nbsp;</td>
                            <td style={pS.td} /><td style={pS.td} /><td style={pS.td} />
                            {!isPV && <><td style={pS.td} /><td style={pS.td} /><td style={pS.td} /></>}
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* ── Totals & remarks ─────────────────────────────────────────── */}
            <section style={{ width: "100%", marginTop: "20px", borderTop: "1px solid #000" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    {/* Remarks */}
                    <div style={pS.remarks}>
                        <div style={{ fontWeight: "bold", fontSize: 10, textTransform: "uppercase", marginBottom: 5 }}>
                            REMARKS / NOTES
                        </div>
                        <div style={{ whiteSpace: "pre-line" }}>{data.notes || "Thank you."}</div>
                    </div>

                    {/* Totals box */}
                    <div style={{ width: "240px", flexShrink: 0, paddingTop: "10px" }}>
                        {isPV ? (
                            <div style={pS.grandTotalRow}>
                                <span>{grandTotalLabel}</span>
                                <span style={{ textAlign: "right" }}>
                                    {Number(displayGrandTotal).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                            </div>
                        ) : (
                            <>
                                <div style={pS.totalRow}>
                                    <span style={pS.totalLabel}>Subtotal</span>
                                    <span style={{ textAlign: "right" }}>
                                        {Number(displaySubtotal).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                                {taxRate > 0 && (
                                    <div style={pS.totalRow}>
                                        <span style={pS.totalLabel}>SST ({taxRate}%)</span>
                                        <span style={{ textAlign: "right" }}>
                                            {Number(displayTaxAmount).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                )}
                                <div style={pS.grandTotalRow}>
                                    <span>{grandTotalLabel}</span>
                                    <span style={{ textAlign: "right" }}>
                                        {Number(displayGrandTotal).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </section>

            {/* ── Signatures ────────────────────────────────────────────────── */}
            <section style={pS.signatureSection}>
                <div style={pS.signatureBox}>
                    <div>PREPARED BY</div>
                    <div style={pS.signatureLine}>( Company Stamp &amp; Signature )</div>
                </div>
                <div style={pS.signatureBox}>
                    <div>RECEIVED / APPROVED BY</div>
                    <div style={pS.signatureLine}>( Authorized Signatory )</div>
                </div>
            </section>

            <footer style={pS.footer}>
                This is a computer-generated document. No signature is required.
            </footer>
        </div>
    );
});